import { Stack } from '@mui/material'
import { isAxiosError } from 'axios'
import React, { useCallback, useState } from 'react'

import { LoadingButton } from 'components/common/LoadingButton'
import { TestEvaluationResultsStatusCheckIntegration } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { ResponseAlertNotification } from 'components/status-checks/common/ResponseAlertNotification'
import { ResponseBodyField } from 'components/status-checks/common/ResponseBodyField'
import { ResponseCodeField } from 'components/status-checks/common/ResponseCodeField'
import { useValidateAllConfigurations } from 'components/status-checks/hooks/use-validate-all-configurations'
import { type StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'
import {
  type StatusCheckDetailsContextValues,
  useStatusCheckDetailsContext
} from 'context/status-checks/StatusCheckDetailsContext'
import { type EvaluationResults } from 'types/api/status-checks/evaluation'
import {
  type EndpointConfiguration,
  type EndpointResponse,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

const TestStatusCheck: React.FC<{
  disabled: boolean
  integration: TeamExternalIntegration
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  onSuccess: StatusCheckDetailsContextValues['handleSuccess']
}> = ({
  disabled,
  integration,
  endpointConfiguration,
  evaluationConfiguration,
  onSuccess
}) => {
  const {
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  /*
   * NOTE: Pressing the 'cancel' action can trigger the cancellation request, but we cannot know when this
   * completes from the controller instance alone. We can reset this flag once we receive a 'CancelError'
   * instance from the validation hooks through an effect implemented below.
   */
  const [isCanceling, setIsCanceling] = useState(false)

  // Response details state
  const [authResponseCode, setAuthResponseCode] = useState<number | null>(null)
  const [authResponseBody, setAuthResponseBody] = useState<string | null>(null)
  const [endpointResponse, setEndpointResponse] = useState<EndpointResponse>()
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResults>()

  const {
    abortController,
    hasError,
    isCanceled,
    isValidating,
    isValidated,
    resetValidations,
    validateEndpointAndEvaluationConfigurations
  } = useValidateAllConfigurations({
    endpointConfiguration,
    evaluationConfiguration,
    integration
  })

  // Flags for various action button states
  const isTestActionDisabled = disabled || isValidating
  const isCancelActionDisabled = !isValidating || isCanceling

  const resetAuthResponseDetails = useCallback(() => {
    setAuthResponseCode(null)
    setAuthResponseBody(null)
    setEndpointResponse(undefined)
    setEvaluationResults(undefined)
    setIsStatusCheckConnectionValidated(false)
    setIsStatusCheckEvaluationValidated(false)
  }, [setIsStatusCheckConnectionValidated, setIsStatusCheckEvaluationValidated])

  const handleCancelTest = () => {
    setIsCanceling(true)
    abortController.abort()
  }

  /*
   * Must go through the process of evaluating both:
   * 1. Endpoint Configuration Validation
   * 2. Evaluation Configuration Validation
   * As each step validates, context flags are updated to reflect the validation state.
   */
  const handleTestHealthCheck = async () => {
    // Clear previous response details and mutation states when initiating a new query.
    resetAuthResponseDetails()
    resetValidations()

    try {
      // First, validate the endpoint configuration. The hook handles PNI vs public integration cases internally.
      const [endpointResponse, evaluationResults] =
        await validateEndpointAndEvaluationConfigurations()

      // Update context flags to reflect successful endpoint/evaluation validation
      setIsStatusCheckConnectionValidated(true)
      setIsStatusCheckEvaluationValidated(true)

      // Update local state with the results
      setEndpointResponse(endpointResponse)
      setEvaluationResults(evaluationResults)

      /*
       * NOTE: `onSuccess` here is meant to refer to the endpoint evaluation phase.
       * Looking upstream, this sets the `externalIntegration.lastAuthenticatedResponse` field in the form context.
       */
      onSuccess(endpointResponse, evaluationResults)
    } catch (error: unknown) {
      // In the event this is not an axios error, at least throw an error toast notification to inform the user.
      if (isAxiosError(error)) {
        const { response } = error
        const { data = null, status } = response || {}

        setAuthResponseBody(data)
        setAuthResponseCode(status ?? null)
        setIsStatusCheckConnectionValidated(false)
        setIsStatusCheckEvaluationValidated(false)
      }
    } finally {
      setIsCanceling(false)
    }
  }

  return (
    <Stack data-test-status-check spacing={2}>
      <Stack direction="row" spacing={2}>
        <LoadingButton
          data-testid="test-status-check"
          isDisabled={isTestActionDisabled}
          isLoading={isValidating}
          size="small"
          onClick={handleTestHealthCheck}
        >
          {isValidating ? 'Testing Health Check...' : 'Test Health Check'}
        </LoadingButton>
        <LoadingButton
          color="secondary"
          data-testid="cancel-test-status-check"
          isDisabled={isCancelActionDisabled}
          isLoading={isCanceling}
          size="small"
          variant="contained"
          onClick={handleCancelTest}
        >
          {isCanceling ? 'Canceling Test...' : 'Cancel Test'}
        </LoadingButton>
      </Stack>
      {hasError && !isCanceled && (
        <ResponseAlertNotification alertSeverity="error">
          Monitor URL returned an error or was unreachable. See response details
          below.
        </ResponseAlertNotification>
      )}
      {authResponseCode && (
        <ResponseCodeField httpStatusCode={authResponseCode} />
      )}
      {authResponseBody && (
        <ResponseBodyField responseBody={authResponseBody} />
      )}
      {isValidated && evaluationResults && endpointResponse && (
        <TestEvaluationResultsStatusCheckIntegration
          endpointResponse={endpointResponse}
          evaluationResults={evaluationResults}
          integration={integration}
        />
      )}
    </Stack>
  )
}

export default TestStatusCheck
